import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faWandMagicSparkles, faTriangleExclamation, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import './EditCampaignModal.css';
import './GroupEditModal.css';

/**
 * One prompt applied across several Google Ads campaigns.
 *
 * PREVIEW IS THE DEFAULT, NOT AN OPTION
 * A group edit fans out, and Google Ads has no undo. So the primary action runs
 * a dry run: the planner selects campaigns and values, the judge reviews them,
 * and nothing is mutated. Applying is a second, deliberate click on a plan the
 * user has read. That ordering is the whole safety story of this modal — a
 * single "Apply" button on a prompt box would be a bulk write to live campaigns
 * from a sentence nobody checked.
 */

/** Destinations the Google Ads group editor plans for. */
const GROUP_EDIT_DESTINATIONS = new Set([
  'google-ads-search', 'google_search', 'g-ads-search', 'search',
  'google-ads-pmax', 'google_pmax', 'g-ads-pmax', 'pmax',
  'google-ads-demandgen', 'google_demandgen', 'g-ads-demandgen',
  'demandgen', 'demand-gen',
]);

export const isGroupEditable = (campaign) => GROUP_EDIT_DESTINATIONS.has(
  String(campaign?.destination || '').toLowerCase());

/** Prompts that map onto what the planner can actually execute. */
const EXAMPLE_PROMPTS = [
  'Raise the daily budget to 750 on the NRI campaigns',
  'Set a target CPA of 250 on every Search campaign',
  'Add a structured snippet listing NRE Account, NRO Account and FCNR Deposit',
  'Add a new responsive search ad to the brand ad groups leading with 6.5% p.a.',
];

/**
 * The reason the server gave, not axios's description of the status code.
 *
 * `err.message` on a rejected axios call is "Request failed with status code
 * 400" — true, and useless. Every 400 this route returns carries a specific,
 * actionable reason in the body ("Every campaign in a group edit must belong to
 * the same Google Ads account"), plus the ids or names it applies to. Showing
 * the status line instead threw all of that away and left the user with nothing
 * to act on.
 */
export const describeRequestError = (err) => {
  const body = err?.response?.data;
  if (!body) return err?.message || 'The group edit could not be started';

  const reason = body.error || err.message || 'The group edit could not be started';
  // The route names the offending campaigns in a field per failure mode; append
  // whichever one came back so the message says WHICH campaigns to uncheck.
  const named = [
    ...(body.unsupported || []).map((c) => c.name || c.id),
    ...(body.unlaunched || []).map((c) => c.name || c.id),
    ...(body.missing || []),
  ].filter(Boolean);
  return named.length ? `${reason}: ${named.join(', ')}` : reason;
};

const GroupEditModal = ({ isOpen, onClose, campaigns = [], onPreview, onApply }) => {
  const [prompt, setPrompt] = useState('');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'preview' | 'apply' | null

  const { editable, skipped, accountConflict } = useMemo(() => {
    const ok = campaigns.filter(isGroupEditable);
    // The Python editor authenticates once and mutates every campaign in the
    // plan under that one customer id, so the route refuses a mixed selection.
    // Catching it here means the user is told which campaigns clash while they
    // can still uncheck one, instead of after a round trip that only says 400.
    const channels = [...new Set(ok.map((c) => String(c.channel_id || '')))];
    return {
      editable: ok,
      skipped: campaigns.filter((c) => !isGroupEditable(c)),
      accountConflict: channels.length > 1,
    };
  }, [campaigns]);

  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setPlan(null);
      setError(null);
      setBusy(null);
    }
  }, [isOpen]);

  // A changed prompt invalidates the plan it produced. Without this the Apply
  // button would still be holding the previous plan while the box shows new
  // text — the user would be applying something other than what they read.
  const handlePromptChange = (value) => {
    setPrompt(value);
    if (plan) setPlan(null);
  };

  if (!isOpen) return null;

  const run = async (mode) => {
    setBusy(mode);
    setError(null);
    try {
      const action = mode === 'preview' ? onPreview : onApply;
      const result = await action({
        campaignIds: editable.map((c) => c.id),
        description: prompt.trim(),
      });
      if (mode === 'preview') {
        setPlan(result);
      } else {
        onClose(result);
      }
    } catch (err) {
      setError(describeRequestError(err));
    } finally {
      setBusy(null);
    }
  };

  const canPreview = prompt.trim().length > 0 && editable.length > 0
    && !accountConflict && !busy;

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content group-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            {' '}Group edit
          </h2>
          <button className="modal-close" onClick={() => onClose()} disabled={!!busy}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <p className="group-edit-scope">
            {editable.length} campaign{editable.length === 1 ? '' : 's'} selected
          </p>
          <ul className="group-edit-campaigns">
            {editable.map((c) => (
              <li key={c.id}>
                <span className="group-edit-campaign-name">{c.name}</span>
                <span className="group-edit-campaign-dest">{c.destination}</span>
              </li>
            ))}
          </ul>

          {skipped.length > 0 && (
            <div className="group-edit-warning">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                {skipped.length} selected campaign{skipped.length === 1 ? ' is' : 's are'} not
                Google Ads Search, Performance Max or Demand Gen and will not be edited:
                {' '}{skipped.map((c) => c.name).join(', ')}
              </span>
            </div>
          )}

          {accountConflict && (
            <div className="group-edit-error">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                These campaigns are in different Google Ads accounts. One edit runs
                against one account, so select campaigns from a single account.
              </span>
            </div>
          )}

          <label className="group-edit-label" htmlFor="group-edit-prompt">
            What should change?
          </label>
          <textarea
            id="group-edit-prompt"
            className="group-edit-prompt"
            rows={3}
            value={prompt}
            placeholder="Raise the daily budget to 750 on the NRI campaigns"
            onChange={(e) => handlePromptChange(e.target.value)}
            disabled={!!busy}
          />

          {!plan && (
            <div className="group-edit-examples">
              {EXAMPLE_PROMPTS.map((example) => (
                <button
                  key={example}
                  type="button"
                  className="group-edit-example"
                  onClick={() => handlePromptChange(example)}
                  disabled={!!busy}
                >
                  {example}
                </button>
              ))}
            </div>
          )}

          {plan && (
            <div className="group-edit-plan">
              <h3>
                <FontAwesomeIcon icon={faCheck} />
                {' '}Planned change
              </h3>
              <p className="group-edit-summary">{plan.summary || plan.message}</p>
              {Array.isArray(plan.notes) && plan.notes.length > 0 && (
                <ul className="group-edit-notes">
                  {plan.notes.map((note) => <li key={note}>{note}</li>)}
                </ul>
              )}
              <p className="group-edit-targets">
                Applies to {plan.campaigns_targeted?.length ?? 0} of {editable.length} selected
                campaigns.
              </p>
              {plan.judge?.issues?.length > 0 && (
                <ul className="group-edit-notes">
                  {plan.judge.issues.map((issue) => <li key={issue}>{issue}</li>)}
                </ul>
              )}
            </div>
          )}

          {error && (
            <div className="group-edit-error">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => onClose()} disabled={!!busy}>
            Cancel
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => run('preview')}
            disabled={!canPreview}
          >
            {busy === 'preview'
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Planning…</>
              : 'Preview changes'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => run('apply')}
            // Apply is only reachable from a plan the user has read. It is not a
            // convenience gate — it is what stops a sentence becoming a bulk
            // write to live campaigns without review.
            disabled={!plan || !!busy}
            title={plan ? 'Apply the planned change' : 'Preview the change first'}
          >
            {busy === 'apply'
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Applying…</>
              : 'Apply to campaigns'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupEditModal;
